import React, { useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    Typography,
    Paper,
    Snackbar,
    Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { forgetPassword } from 'redux/actions/PasswordAction';
import { useDispatch } from 'react-redux';
import Loader from 'ui-component/Loader';

const ForgetPassword = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    return (
        <Box
        sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f3f6fb',
            position: 'relative' // make relative for overlay positioning
        }}
    >
        {loading && (
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    bgcolor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Loader />
            </Box>
        )}

        <Paper
            elevation={3}
            sx={{
                width: '100%',
                maxWidth: 400,
                padding: 4,
                borderRadius: 3,
                zIndex: 1 // below loader overlay
            }}
        >
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h3" sx={{ color: '#6B3FA0' }}>
                    Forgot your password?
                    <br />
                    <Typography variant="subtitle1" component="div">
                        Enter your email to resend it.
                    </Typography>
                    </Typography>
                </Box>

                <Formik
                    initialValues={{ email: '', submit: null }}
                    validationSchema={Yup.object().shape({
                        email: Yup.string().email('Must be a valid email').max(255).required('Email is required')
                    })}
                    onSubmit={async (values, { setSubmitting, resetForm }) => {
                        setLoading(true);

                        dispatch(
                            forgetPassword(
                                { email: values.email },
                                (res) => {
                                    setSnackbar({
                                        open: true,
                                        message: res.data,
                                        severity: res.color === 'success' ? 'success' : 'error'
                                    });
                                    resetForm();
                                    if (res.color === 'success') {
                                        setTimeout(() => {
                                            navigate('/login');
                                        }, 3000); // wait 3 seconds so user sees success
                                    }
                                },
                                (err) => {
                                    setSnackbar({
                                        open: true,
                                        message: err.data,
                                        severity: 'error'
                                    });
                                },
                                () => {
                                    setLoading(false);
                                    setSubmitting(false);
                                }
                            )
                        );
                    }}
                >
                    {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                        <form noValidate onSubmit={handleSubmit}>
                            <FormControl
                                fullWidth
                                error={Boolean(touched.email && errors.email)}
                                sx={{ ...theme.typography.customInput }}
                            >
                                <InputLabel htmlFor="email-forgot">Email Address</InputLabel>
                                <OutlinedInput
                                    id="email-forgot"
                                    type="email"
                                    value={values.email}
                                    name="email"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    label="Email Address"
                                />
                                {touched.email && errors.email && (
                                    <FormHelperText error id="helper-text-email-forgot">
                                        {errors.email}
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <Box sx={{ mt: 2 }}>
                                <AnimateButton>
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        disabled={isSubmitting || loading}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        {loading ? 'Sending...' : 'Send Password'}
                                    </Button>
                                </AnimateButton>
                            </Box>

                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="text"
                                    color="primary"
                                    onClick={() => navigate('/login')}
                                    size="small"
                                >
                                    Back to Login
                                </Button>
                            </Box>
                        </form>
                    )}
                </Formik>
            </Paper>

            {/* Snackbar Notification */}
            
            <Snackbar
    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    open={snackbar.open}
    autoHideDuration={3000}
    onClose={handleCloseSnackbar}
>
    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
        {snackbar.message}
    </Alert>
</Snackbar>
        </Box>
    );
};

export default ForgetPassword;
 